import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, Check, X } from "lucide-react";
import { THEMES, useTheme } from "../../theme/ThemeProvider";

const PANEL_INITIAL = { opacity: 0, y: 16, scale: 0.96 };
const PANEL_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const PANEL_TRANSITION = { duration: 0.3, ease: [0.16, 1, 0.3, 1] };
const BTN_HOVER = { scale: 1.05 };
const BTN_TAP = { scale: 0.95 };

export const ThemeSwitcher = () => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-[120]" data-testid="theme-switcher">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={PANEL_INITIAL}
            animate={PANEL_ANIMATE}
            exit={PANEL_INITIAL}
            transition={PANEL_TRANSITION}
            className="mb-3 w-64 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-soft)/0.8)] p-4 backdrop-blur-2xl shadow-2xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--muted))]">
                Choose a palette
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="theme-close-btn"
                className="text-[hsl(var(--muted))] hover:text-[hsl(var(--fg))]"
              >
                <X size={15} />
              </button>
            </div>
            <div className="space-y-1.5">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`theme-option-${t.id}`}
                  onClick={() => setTheme(t.id)}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-300 hover:bg-[hsl(var(--fg)/0.06)]"
                >
                  <span
                    className="h-6 w-6 shrink-0 rounded-full border border-white/20"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, ${t.dot}, ${t.bg})`,
                    }}
                  />
                  <span className="flex-1 text-sm text-[hsl(var(--fg))]">
                    {t.name}
                  </span>
                  {theme === t.id && (
                    <Check size={15} className="text-[hsl(var(--primary))]" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={BTN_HOVER}
        whileTap={BTN_TAP}
        onClick={() => setOpen((o) => !o)}
        data-testid="theme-toggle-btn"
        className="flex h-14 w-14 items-center justify-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] shadow-xl"
        aria-label="Change theme"
      >
        <Palette size={22} />
      </motion.button>
    </div>
  );
};
